import _ from 'lodash';
import { observable, action, computed } from 'mobx';
import config from 'client/shared/config';

const { paths } = config;

export default class Album {
	store = null;

	constructor (store, data) {
		this.store = store;

		if (_.isObject(data)) {

			this.update(data);
		}
	}

	@observable
	_id = null;

	@computed
	get id () {
		return this._id;
	}

	set id (id) {
		this._id = id;
	}

	/**
	 * Observable Properties
	 */
	@observable
 	_imageData = null;

 	get imageData () {
 		return this._imageData;
 	}

 	set imageData (imageData) {
 		this._imageData = imageData;
 	}

	@observable
	_artistID = '';

	@computed
	get artistID () {
		return this._artistID;
	}

	set artistID (artistID) {
		this._artistID = artistID;
	}

	@observable
	_name = '';

	@computed
	get name () {
		return this._name;
	}

	set name (name) {
		this._name = name;
	}

	@observable
	_linerNotes = '';

	@computed
	get linerNotes () {
		return this._linerNotes;
	}

	set linerNotes (linerNotes) {

		if (_.isString(linerNotes)) {

			this._linerNotes = linerNotes;
		}
	}

	@observable
	_artwork = '';

	@computed
	get artwork () {
		return this._artwork;
	}

	set artwork (artwork) {
		this._artwork = artwork;
	}

	@computed
	get artworkPath () {
		// An empty string is a string but yields ${base}/ — which 404s the
		// asset bucket. Treat empty as "no artwork yet".
		if (!_.isString(this.artwork) || _.isEmpty(this.artwork)) {
			return '';
		}
		return `${paths.albumImgBasePath}/${this.artwork}`;
	}

	@observable
	_songs = [];

	@computed
	get songs () {
		const { _songs, store } = this;

		if (_.isEmpty(_songs)) {

			return [];
		}

		return store.songsStore.getSongByID(_songs);
	}

	set songs (songs = []) {
		this._songs = songs.map(({ id }) => id);
	}

	@computed
	get serialized () {
		const songs = _.map(this._songs, (val) => _.isString(val) ? val : val._id);

		return {
			songs,
			_id: this.id,
			artistID: this._artistID,
			name: this._name,
			artwork: this._artwork,
			imageData: this._imageData,
			linerNotes: this._linerNotes
		};
	}

	/**
	 * Methods
	 */

	async delete () {
		await this.store.delete(this);
	}

	async save ({ artist, user }) {
		await this.store.saveAlbum({
			album: this,
			artist, user
		});
	}

	async saveArtwork ({ artist, user }) {
		await this.store.saveAlbumArtwork({
			user,
			artist,
			album: this,
			imageData: this.imageData
		});
	}

	@action
	addSong (song) {
		const id = _.isString(song) ? song : song?.id ?? song?._id;
		if (_.isNil(id) || id === '') {

			return;
		}
		const sid = String(id);
		const exists = this._songs.some((s) => {
			const cur = _.isString(s) ? String(s) : String(s._id);

			return cur === sid;
		});
		if (exists) {

			return;
		}
		this._songs.push(sid);
	}

	@action
	removeSong (songId) {
		if (_.isNil(songId) || songId === '') {

			return;
		}
		const id = String(songId);
		this._songs = this._songs.filter((s) => {
			const sid = _.isString(s) ? String(s) : String(s._id);

			return sid !== id;
		});
	}

	@action
	reorderSongs ({ source, destination }) {
		if (!destination) {

			return;
		}
		const next = this._songs.slice();
		const [ removed ] = next.splice(source.index, 1);

		next.splice(destination.index, 0, removed);
		this._songs = next;
	}

	@action
	update ({
		_id,
		artistID,
		name,
		artwork,
		linerNotes,
		songs
	}) {
		this._id = _.defaultTo(_id, this.id);
		this._artistID = _.defaultTo(artistID, this.artistID);
		this._name = _.defaultTo(name, this.name);
		this._artwork = _.defaultTo(artwork, this.artwork);
		this._linerNotes = _.defaultTo(linerNotes, this.linerNotes);
		this._songs = _.defaultTo(songs, this.songs);
	}
}
